import React from 'react';
import { Route } from 'react-router-dom';

import { lazy } from '@components/common/routeLoading';
import { Path } from '@components/routes/constants';
import { RedirectRoute } from '@components/routes/redirect';
import { entitled } from '@components/routes/routeEntitlement';
import type { UseIsRouteEnabled } from '@components/routes/routeEntitlement';
import { useHasProfiles } from '@components/routes/entitlements';
import { ACTION, SECTION } from '@hooks/useDocumentTitle/constants';

import { canCreateProfilesProject } from './hooks/permissionChecks';

const PROFILES_NOT_FOUND_MESSAGE =
  'Profiles project not found or insufficient privileges. Redirecting to Profiles homepage.';

/**
 * Kept local rather than in @components/routes/entitlements so the shared module
 * does not take a dependency on this feature's permission checks.
 */
const useCanCreateProfilesProject: UseIsRouteEnabled = () => canCreateProfilesProject().isAllowed;

/**
 * Only for gates on pages INSIDE the section. The section root must not use it:
 * its own path is the redirect target, so an unentitled visitor would be sent to
 * the URL they are already on and left on a blank pane.
 */
const PROFILES_REDIRECT = { to: Path.PROFILES, message: PROFILES_NOT_FOUND_MESSAGE };

/**
 * Every element is lazy — see the note in @components/cohorts/routes on why the
 * route definitions must not reference page modules directly. These pages were
 * previously plain imports sitting behind one lazy() on the routes module.
 */
const ProfilesLayout = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './routesLayout').then((m) => ({
    default: m.ProfilesLayout,
  })),
);

const ProfilesProjectLayout = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './routesLayout').then((m) => ({
    default: m.ProfilesProjectLayout,
  })),
);

const WhtProjectsList = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './whtProjectsList').then((m) => ({
    default: m.WhtProjectsList,
  })),
);

const WhtProjectView = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './whtProjectView').then((m) => ({
    default: m.WhtProjectView,
  })),
);

const AddProject = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './addProject').then((m) => ({
    default: m.AddProject,
  })),
);

const ConvertProject = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './addProject/convertProject').then((m) => ({
    default: m.ConvertProject,
  })),
);

const CopilotSuccess = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './addProject/steps/copilotSuccessPage').then((m) => ({
    default: m.CopilotSuccess,
  })),
);

const EditProjectFlow = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './editProject').then((m) => ({
    default: m.EditProjectFlow,
  })),
);

const CopilotEditFlow = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './copilotEditFlow').then((m) => ({
    default: m.CopilotEditFlow,
  })),
);

const EditorPage = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './editorPage').then((m) => ({
    default: m.EditorPage,
  })),
);

const CohortDetails = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './whtProjectView/cohorts').then((m) => ({
    default: m.CohortDetails,
  })),
);

const EntityView = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './entityView').then((m) => ({
    default: m.EntityView,
  })),
);

const ActivationFlow = lazy(() =>
  import(/* webpackChunkName: "profiles" */ './activation').then((m) => ({
    default: m.ActivationFlow,
  })),
);

const SourceView = lazy(() =>
  import(/* webpackChunkName: "sources.source-view" */ '@components/sources/source-view').then(
    (m) => ({
      default: m.SourceView,
    }),
  ),
);

/** See the note in @components/botManagement/routes on why these are definitions. */
export const profilesRoutes = (
  <Route
    key={Path.PROFILES}
    path={Path.PROFILES}
    element={entitled(useHasProfiles, <ProfilesLayout />)}
    handle={{ section: SECTION.profiles }}
  >
    <Route index element={<WhtProjectsList />} />
    <Route
      path="setup"
      element={entitled(useCanCreateProfilesProject, <AddProject />, PROFILES_REDIRECT)}
      handle={{ action: ACTION.create }}
    />
    {/* `:id` is a layout route so the project name is published once for the
        whole subtree — every page below reads `<project> - Profiles`. */}
    <Route path=":id" element={<ProfilesProjectLayout />}>
      <Route index element={<WhtProjectView />} />
      <Route path="convert" element={<ConvertProject />} />
      <Route path="edit" element={<EditProjectFlow />} />
      <Route path="copilot-edit" element={<CopilotEditFlow />} />
      <Route path="copilot-success" element={<CopilotSuccess />} />
      <Route path="editor" element={<EditorPage />} />
      <Route path="entities/:entityId/cohorts/:cohortId" element={<CohortDetails />} />
      <Route
        path="entities/:entityId/cohorts/:cohortId/sources/:sourceId"
        element={<SourceView />}
      />
      <Route path="entities/:entityId/cohorts/:cohortId/activate" element={<ActivationFlow />} />
      <Route path="entities/:entity" element={<EntityView />} />
    </Route>
    <Route
      path="*"
      element={<RedirectRoute to={Path.PROFILES} message={PROFILES_NOT_FOUND_MESSAGE} />}
    />
  </Route>
);
